'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useInfiniteQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthQueryKey, AuthQueryOptions } from '@/api';
import { createMarketingConsentDialog } from '@/app/_components/MarketingConsentSheet';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useAcceptMarketingConsent } from '@/hooks/useAcceptMarketingConsent';
import { useAuthStore } from '@/stores/AuthStore';
import { closeResponsiveDialog, openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { getStaticCdnUrl } from '@/utils';
import { useGiftTrialGate } from '../_hooks/useGiftTrialGate';
import { buildCampaignListOptions } from '../_utils/campaignListQuery';
import { retryUnlessReturnCode } from '../_utils/retryUnlessReturnCode';
interface NewCampaignAlarmProps {
  isServerLogin: boolean;
}
const BELL_IMAGE_SRC = getStaticCdnUrl('gift-trial/bell.webp');
export const NewCampaignAlarm = t0 => {
  const $ = _c(3);
  const {
    isServerLogin
  } = t0;
  const isLogin = useAuthStore(_temp) ?? isServerLogin;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...buildCampaignListOptions("RECRUITING"),
      select: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: recruitingCampaignCount
  } = useInfiniteQuery(t1);
  if (recruitingCampaignCount !== 0) {
    return null;
  }
  if (!isLogin) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AlarmTrigger />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ErrorBoundary fallback={null}><Suspense fallback={null}><UnsubscribedAlarmTrigger /></Suspense></ErrorBoundary>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const UnsubscribedAlarmTrigger = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthQueryOptions["get:/wello/v2/auth/gift-trial/me"](),
      retry: retryUnlessReturnCode,
      select: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: isSubscribed
  } = useSuspenseQuery(t0);
  if (isSubscribed) {
    return null;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlarmTrigger />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const AlarmTrigger = () => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  const triggerGate = useGiftTrialGate();
  const acceptConsent = useAcceptMarketingConsent();
  let t0;
  if ($[0] !== acceptConsent || $[1] !== queryClient) {
    t0 = () => {
      openResponsiveDialog(createMarketingConsentDialog({
        title: "\uCCB4\uD5D8\uB2E8\uC5D0 \uCC38\uC5EC\uD558\uB824\uBA74 \uB3D9\uC758\uAC00 \uD544\uC694\uD574\uC694!",
        agreeLabel: "\uB3D9\uC758\uD558\uACE0 \uC2DC\uC791\uD558\uAE30",
        successMessage: "\uCCB4\uD5D8\uB2E8 \uBAA8\uC9D1 \uC18C\uC2DD\uC744 \uC571\uC5D0\uC11C \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694",
        isCloseButtonHidden: true,
        onAgree: () => acceptConsent.mutateAsync().then(() => {
          closeResponsiveDialog();
          return queryClient.invalidateQueries({
            queryKey: AuthQueryKey["get:/wello/v2/auth/gift-trial/me"]()
          });
        })
      }));
    };
    $[0] = acceptConsent;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const openConsentDialog = t0;
  let t1;
  if ($[3] !== openConsentDialog || $[4] !== triggerGate) {
    t1 = () => triggerGate(DONATION_ROUTES.GIFT_TRIAL.pathname, openConsentDialog);
    $[3] = openConsentDialog;
    $[4] = triggerGate;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleClick = t1;
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex h-48 w-172 shrink-0 items-center justify-center gap-8 rounded-small bg-O-300 font-700 text-Gray-white typo-caption1", "transition-colors hover:bg-O-400 active:bg-O-500", FOCUS_RING, "mobile-tablet:hidden");
    t3 = <Image src={BELL_IMAGE_SRC} alt="" width={24} height={24} />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleClick) {
    t4 = <button onClick={handleClick} className={t2}>{t3}오픈 알림 받기</button>;
    $[8] = handleClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(data) {
  return data.pages[0]?.context?.recruitingCampaignCount;
}
function _temp3(t0) {
  const {
    context
  } = t0;
  return context?.profile.recruitNoticeConsent ?? false;
}